import { useQuery } from '@tanstack/react-query';
import { QRCodeSVG } from 'qrcode.react';
import { Alert, Button, Card, FuntourMark, PageTitle, PasswordField, SelectField, TextField } from '@rooming/ui';
import { isProblem, useCountdown } from '@rooming/web-core';
import { useState, type SyntheticEvent } from 'react';
import { backoffice } from '../api';
import { messageOf } from '../common';
import { useSession } from '../session';

/** Step 1 — email and password. The error never says which one was wrong (anti-enumeration). */
export function LoginPage({ onNext }: { readonly onNext: (step: 'MFA_ENROLL' | 'MFA_VERIFY') => void }) {
  const { setStage } = useSession();
  // The passengers' site is the same address without «-admin» (rooming.funtour.cl / rooming-admin.funtour.cl).
  const passengerSiteUrl = `${window.location.protocol}//${window.location.host.replace(/^rooming-admin\./, 'rooming.')}`;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  // 429 with Retry-After: the shared countdown keeps the button disabled (web-core).
  const [waitSeconds, setWaitSeconds] = useCountdown();
  // A hotel or a guide that works with both countries has an account in each (owner, 2026-10-07): the API asks which
  // one only after a correct password. undefined = not asked; '' = asked, not chosen yet.
  const [country, setCountry] = useState<'' | 'cl' | 'py'>();

  async function submit(event: SyntheticEvent): Promise<void> {
    event.preventDefault();
    setError(undefined);
    setBusy(true);
    try {
      const result = await backoffice.login(email.trim(), password, country === '' ? undefined : country);
      setPassword('');
      // Hotel users and guides have no second factor (DR-205): their session is complete after the password…
      if (result.nextStep === 'NONE') {
        setStage('ACTIVE');
        return;
      }
      // …unless an administrator set a provisional password: they must choose their own first (prompt11 §14).
      if (result.nextStep === 'CREDENTIAL_CHANGE') {
        setStage('CREDENTIAL_CHANGE_PENDING');
        return;
      }
      onNext(result.nextStep);
      setStage('MFA_PENDING');
    } catch (problem) {
      if (isProblem(problem) && problem.slug === 'elegir-pais') {
        setCountry('');
        return;
      }
      setError(messageOf(problem));
      if (isProblem(problem) && problem.retryAfterSeconds !== undefined) setWaitSeconds(problem.retryAfterSeconds);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto w-full max-w-md py-2 sm:py-8">
      <div className="rounded-3xl border border-line bg-surface p-6 shadow-xl sm:p-8">
        <div className="mb-6 flex items-center gap-3">
          <FuntourMark size={44} />
          <b className="text-lg">Funtour</b>
        </div>
        <p className="text-sm font-bold tracking-widest text-brand-700 uppercase">Bienvenido</p>
        <h1 className="mt-2 mb-6 text-3xl font-bold">Ingresar</h1>
        <form className="flex flex-col gap-4" onSubmit={(event) => void submit(event)} noValidate>
          <TextField
            label="Correo electrónico"
            name="email"
            type="email"
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            required
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              setCountry(undefined);
            }}
          />
          {email.trim() !== '' && !email.includes('@') && (
            <Alert tone="info" title="Eso no parece un correo">
              Acá entran el personal de Funtour, los hoteles y los guías, con su correo. Si sos pasajero, firmante o
              acompañante, ingresá con tu usuario en{' '}
              <a className="font-semibold underline" href={passengerSiteUrl}>
                {passengerSiteUrl.replace('https://', '')}
              </a>
              .
            </Alert>
          )}
          <PasswordField
            label="Contraseña"
            name="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
            }}
          />
          {country !== undefined && (
            <SelectField
              label="País"
              placeholder="Elegí el país"
              hint="Tu correo tiene una cuenta en Chile y otra en Paraguay. Elegí con cuál querés ingresar."
              options={[
                { value: 'cl', label: 'Chile' },
                { value: 'py', label: 'Paraguay' },
              ]}
              required
              value={country}
              onChange={(event) => {
                setCountry(event.target.value as '' | 'cl' | 'py');
              }}
            />
          )}
          {error !== undefined && (
            <Alert tone="error">
              {error}
              {waitSeconds > 0 && ` (${String(waitSeconds)} s)`}
            </Alert>
          )}
          <Button
            type="submit"
            fullWidth
            loading={busy}
            disabled={waitSeconds > 0 || email.trim() === '' || password === '' || country === ''}
          >
            Continuar
          </Button>
        </form>
      </div>
    </div>
  );
}

/**
 * Step 2 — the second factor (Funtour staff only; hotel users and guides skip it, prompt10 §13). First login: enrollment (the secret is shown
 * once, then the recovery codes once). Later logins: a 6-digit code or a recovery code.
 */
export function SecondFactorPage({ step }: { readonly step: 'MFA_ENROLL' | 'MFA_VERIFY' | undefined }) {
  const { setStage, signOut } = useSession();
  const [code, setCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>();
  // After a reload the step is unknown: try verification first; the server says when enrollment is needed instead.
  const [mode, setMode] = useState<'MFA_ENROLL' | 'MFA_VERIFY'>(step ?? 'MFA_VERIFY');
  const enrollment = useQuery({
    queryKey: ['mfa-enrollment'],
    queryFn: backoffice.startEnrollment,
    enabled: mode === 'MFA_ENROLL' && recoveryCodes === undefined,
    staleTime: Infinity,
    retry: false,
  });

  async function submit(event: SyntheticEvent): Promise<void> {
    event.preventDefault();
    setError(undefined);
    setBusy(true);
    try {
      if (mode === 'MFA_ENROLL') {
        const result = await backoffice.confirmEnrollment(code.trim());
        setRecoveryCodes(result.recoveryCodes);
      } else {
        await backoffice.verify(useRecovery ? { recoveryCode: code.trim() } : { code: code.trim() });
        setStage('ACTIVE');
      }
      setCode('');
    } catch (problem) {
      if (isProblem(problem, 'sesion-vencida')) {
        await signOut();
        return;
      }
      // "Configure it first": the account has no second factor yet.
      if (isProblem(problem, 'mfa-requerido') && mode === 'MFA_VERIFY') setMode('MFA_ENROLL');
      else setError(messageOf(problem));
    } finally {
      setBusy(false);
    }
  }

  if (recoveryCodes !== undefined) {
    return (
      <div className="mx-auto w-full max-w-md">
        <PageTitle subtitle="Guardalos en un lugar seguro: cada uno sirve una sola vez si perdés tu teléfono.">
          Códigos de recuperación
        </PageTitle>
        <Card className="flex flex-col gap-4">
          <Alert tone="warning">No se vuelven a mostrar.</Alert>
          <ul className="grid grid-cols-1 gap-2 font-mono text-base sm:grid-cols-2">
            {recoveryCodes.map((recovery) => (
              <li key={recovery} className="rounded-lg border border-line bg-canvas px-3 py-2 break-all">
                {recovery}
              </li>
            ))}
          </ul>
          <Button
            fullWidth
            onClick={() => {
              setStage('ACTIVE');
            }}
          >
            Ya los guardé
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-md">
      <PageTitle
        subtitle={
          mode === 'MFA_ENROLL'
            ? 'Configurá tu aplicación de autenticación (Google Authenticator, Authy, 1Password…).'
            : 'Ingresá el código de tu aplicación de autenticación.'
        }
      >
        Verificación en dos pasos
      </PageTitle>
      <Card>
        <form className="flex flex-col gap-4" onSubmit={(event) => void submit(event)} noValidate>
          {mode === 'MFA_ENROLL' && enrollment.data !== undefined && (
            <div className="flex flex-col gap-2">
              <p className="text-base">Escaneá este código con la cámara de tu aplicación de autenticación:</p>
              {/* Drawn in the browser (SVG, no request anywhere): the secret never leaves this screen. White margin
                  and dark modules on white, whatever the theme, so every phone camera reads it. */}
              <div className="self-center rounded-2xl bg-white p-3 shadow-sm">
                <QRCodeSVG
                  value={enrollment.data.otpauthUri}
                  size={208}
                  level="M"
                  marginSize={2}
                  bgColor="#ffffff"
                  fgColor="#0f172a"
                  title="Código QR para la aplicación de autenticación"
                />
              </div>
              <p className="text-sm text-muted">¿No podés escanearlo? Agregá la cuenta a mano con esta clave:</p>
              <p className="rounded-lg border border-line bg-canvas px-3 py-2 font-mono text-base break-all">
                {enrollment.data.secret}
              </p>
              <a
                href={enrollment.data.otpauthUri}
                className="inline-flex min-h-11 items-center text-brand-700 underline underline-offset-4"
              >
                Abrir en mi aplicación de autenticación
              </a>
            </div>
          )}
          {mode === 'MFA_ENROLL' && enrollment.isError && <Alert tone="error">{messageOf(enrollment.error)}</Alert>}
          <TextField
            label={useRecovery ? 'Código de recuperación' : 'Código de 6 dígitos'}
            name="code"
            inputMode={useRecovery ? 'text' : 'numeric'}
            autoComplete="one-time-code"
            required
            value={code}
            onChange={(event) => {
              setCode(event.target.value);
            }}
          />
          {error !== undefined && <Alert tone="error">{error}</Alert>}
          <Button type="submit" fullWidth loading={busy} disabled={code.trim() === ''}>
            Verificar
          </Button>
          {mode === 'MFA_VERIFY' && (
            <Button
              variant="ghost"
              fullWidth
              onClick={() => {
                setUseRecovery((value) => !value);
                setCode('');
              }}
            >
              {useRecovery ? 'Usar el código de la aplicación' : 'Usar un código de recuperación'}
            </Button>
          )}
          <Button variant="ghost" fullWidth onClick={() => void signOut()}>
            Salir
          </Button>
        </form>
      </Card>
    </div>
  );
}

/**
 * Mandatory step for a hotel user or a guide whose password was set by an administrator (prompt11 §14): the provisional
 * password only allows choosing a new one. The session becomes complete with it.
 */
export function OwnPasswordPage() {
  const { setStage, signOut } = useSession();
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const mismatch = repeat !== '' && repeat !== password;

  async function submit(event: SyntheticEvent): Promise<void> {
    event.preventDefault();
    setError(undefined);
    setBusy(true);
    try {
      await backoffice.chooseOwnPassword(password);
      setPassword('');
      setRepeat('');
      setStage('ACTIVE');
    } catch (problem) {
      if (isProblem(problem, 'sesion-vencida')) {
        await signOut();
        return;
      }
      setError(messageOf(problem));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto w-full max-w-md">
      <PageTitle subtitle="Un administrador restableció tu contraseña. Antes de continuar, elegí una que solo vos conozcas.">
        Elegí tu contraseña
      </PageTitle>
      <Card>
        <form className="flex flex-col gap-4" onSubmit={(event) => void submit(event)} noValidate>
          <TextField
            label="Nueva contraseña"
            name="new-password"
            type="password"
            autoComplete="new-password"
            hint="Al menos 12 caracteres. No puede ser la contraseña provisoria."
            required
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
            }}
          />
          <TextField
            label="Repetí la nueva contraseña"
            name="repeat-password"
            type="password"
            autoComplete="new-password"
            required
            error={mismatch ? 'Las contraseñas no coinciden.' : undefined}
            value={repeat}
            onChange={(event) => {
              setRepeat(event.target.value);
            }}
          />
          {error !== undefined && <Alert tone="error">{error}</Alert>}
          <Button type="submit" fullWidth loading={busy} disabled={password.length < 12 || repeat !== password}>
            Guardar y continuar
          </Button>
          <Button variant="ghost" fullWidth onClick={() => void signOut()}>
            Salir
          </Button>
        </form>
      </Card>
    </div>
  );
}
